/*
 * Baddie storefront behaviour for bd- sections.
 * - [data-bd-add] forms add to cart through the same CartLinesUpdateEvent flow as
 *   Horizon's product form, so the cart drawer opens and refreshes itself.
 * - [data-bd-tabs] simple accessible tabs.
 * - .bd-reveal fade-in as sections scroll into view (started when the main thread is idle).
 * - .bd-ticker loops only while on screen.
 */
import { CartLinesUpdateEvent } from '@shopify/events';

document.documentElement.classList.add('bd-js');

const ADDED_STATE_MS = 2200;

/** @param {HTMLFormElement} form */
async function addToCart(form) {
  const button = form.querySelector('[type="submit"]');
  const formData = new FormData(form);
  const quantity = Number(formData.get('quantity')) || 1;

  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((element) => element instanceof HTMLElement && element.dataset.sectionId)
    .filter(Boolean);
  if (sectionIds.length) formData.append('sections', sectionIds.join(','));

  button?.setAttribute('aria-busy', 'true');

  const deferred = CartLinesUpdateEvent.createPromise();
  form.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: String(formData.get('id')), quantity }],
      promise: deferred.promise,
    })
  );

  try {
    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: formData,
    }).then((res) => res.json());

    if (response.status) throw new Error(response.description || response.message || 'Add to cart failed');

    const cart = await fetch(`${Theme.routes.cart_url}.js`).then((res) => res.json());
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'bd-add',
        itemCount: quantity,
        productId: form.dataset.productId,
        sections: response.sections,
        didError: false,
      },
    });

    if (button) {
      button.dataset.state = 'added';
      setTimeout(() => delete button.dataset.state, ADDED_STATE_MS);
    }
  } catch (error) {
    deferred.reject(error);
    const message = form.querySelector('[data-bd-add-error]');
    if (message) {
      message.textContent = error instanceof Error ? error.message : String(error);
      message.hidden = false;
    }
  } finally {
    button?.removeAttribute('aria-busy');
  }
}

document.addEventListener('submit', (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.matches('[data-bd-add]')) return;
  event.preventDefault();
  addToCart(form);
});

/* Pack cards drive Horizon's (hidden) variant picker radios */
document.addEventListener('click', (event) => {
  const pack = event.target instanceof Element ? event.target.closest('[data-bd-packs] [data-option-value]') : null;
  if (!pack || pack.getAttribute('aria-disabled') === 'true') return;
  const scope = pack.closest('.shopify-section') || document;
  const radio = [...scope.querySelectorAll('variant-picker input[type="radio"]')].find(
    (input) => input.value === pack.dataset.optionValue
  );
  for (const other of pack.parentElement.querySelectorAll('[data-option-value]')) {
    other.setAttribute('aria-checked', String(other === pack));
  }
  if (radio && !radio.checked) radio.click();
});

document.addEventListener('change', (event) => {
  const input = event.target;
  if (!(input instanceof HTMLInputElement) || input.type !== 'radio' || !input.closest('variant-picker')) return;
  const scope = input.closest('.shopify-section') || document;
  for (const pack of scope.querySelectorAll('[data-bd-packs] [data-option-value]')) {
    pack.setAttribute('aria-checked', String(pack.dataset.optionValue === input.value));
  }
});

/* Tabs */
document.addEventListener('click', (event) => {
  const tab = event.target instanceof Element ? event.target.closest('[data-bd-tabs] [role="tab"]') : null;
  if (!tab) return;
  const root = tab.closest('[data-bd-tabs]');
  for (const other of root.querySelectorAll('[role="tab"]')) {
    const selected = other === tab;
    other.setAttribute('aria-selected', String(selected));
    other.tabIndex = selected ? 0 : -1;
    const panel = root.querySelector(`#${other.getAttribute('aria-controls')}`);
    if (panel) panel.hidden = !selected;
  }
});

document.addEventListener('keydown', (event) => {
  const tab = event.target instanceof Element ? event.target.closest('[data-bd-tabs] [role="tab"]') : null;
  if (!tab || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
  const tabs = [...tab.closest('[role="tablist"]').querySelectorAll('[role="tab"]')];
  const rtl = document.documentElement.dir === 'rtl';
  let index = tabs.indexOf(tab);
  if (event.key === 'Home') index = 0;
  else if (event.key === 'End') index = tabs.length - 1;
  else index += (event.key === 'ArrowRight') !== rtl ? 1 : -1;
  const next = tabs[(index + tabs.length) % tabs.length];
  next.focus();
  next.click();
  event.preventDefault();
});

/* Reveal */
const revealObserver =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            entry.target.classList.add('is-visible');
            revealObserver.unobserve(entry.target);
          }
        },
        { rootMargin: '0px 0px -8% 0px' }
      )
    : null;

const observeReveals = (root = document) => {
  for (const element of root.querySelectorAll('.bd-reveal:not(.is-visible)')) {
    if (revealObserver) revealObserver.observe(element);
    else element.classList.add('is-visible');
  }
};

// Wait for an idle moment so the observer set-up stays out of the page-load tasks.
const whenIdle = (callback) =>
  'requestIdleCallback' in window ? requestIdleCallback(callback, { timeout: 1500 }) : setTimeout(callback, 200);

whenIdle(() => observeReveals());
document.addEventListener('shopify:section:load', (event) => observeReveals(event.target));

/* Ticker: stop the loop while it is scrolled out of view */
const tickerObserver =
  'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        for (const entry of entries) entry.target.classList.toggle('is-offscreen', !entry.isIntersecting);
      })
    : null;

if (tickerObserver) {
  whenIdle(() => document.querySelectorAll('.bd-ticker').forEach((ticker) => tickerObserver.observe(ticker)));
}
